// src/components/schedules/SchedulesDashboard.tsx
import { FileText, Mail, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useReportList, useReportRecipients, useReportScheduling } from "@/hooks/useReportDelivery";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

const DAYS_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export function SchedulesDashboard() {
  const { data: reports, isLoading: reportsLoading } = useReportList({ active: true });
  const { data: recipients, isLoading: recipientsLoading } = useReportRecipients();
  const { data: schedulings, isLoading: schedulingsLoading } = useReportScheduling();

  const isLoading = reportsLoading || recipientsLoading || schedulingsLoading;

  // Statistiques
  const totalReports = reports?.length || 0;
  const uniqueRecipients = new Set(recipients?.map((r) => r.dest) || []).size;
  const activeSchedulings = schedulings?.filter((s) => s.enabled === 1).length || 0;

  // Prochain envoi
  const now = new Date();
  const currentHour = now.getHours();
  const currentDay = now.getDay() === 0 ? 6 : now.getDay() - 1; // Convertir Dim=0 vers 6

  const nextScheduling = schedulings
    ?.filter((s) => s.enabled === 1)
    .map((s) => {
      const hourDiff = s.send_hour - currentHour;
      const hoursUntil = hourDiff >= 0 ? hourDiff : 24 + hourDiff;
      return { ...s, hoursUntil };
    })
    .sort((a, b) => a.hoursUntil - b.hoursUntil)[0];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cards statistiques */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rapports actifs</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalReports}</div>
            <p className="text-xs text-muted-foreground">Rapports configurés</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Destinataires</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueRecipients}</div>
            <p className="text-xs text-muted-foreground">Emails uniques</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Plannings actifs</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSchedulings}</div>
            <p className="text-xs text-muted-foreground">Envois programmés</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Prochain envoi</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {nextScheduling ? `${nextScheduling.send_hour}h` : "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              {nextScheduling
                ? `Dans ${nextScheduling.hoursUntil}h`
                : "Aucun planning actif"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Planning par heure */}
      <Card>
        <CardHeader>
          <CardTitle>Planning des envois</CardTitle>
          <CardDescription>Répartition des rapports par heure d'envoi</CardDescription>
        </CardHeader>
        <CardContent>
          {activeSchedulings === 0 ? (
            <Alert>
              <AlertDescription>
                Aucun planning actif. Configurez des plannings dans l'onglet "Planning".
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {Array.from({ length: 24 }, (_, hour) => {
                const hourSchedulings = schedulings?.filter(
                  (s) => s.send_hour === hour && s.enabled === 1
                );
                if (!hourSchedulings || hourSchedulings.length === 0) return null;

                return (
                  <div
                    key={hour}
                    className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 text-primary font-bold text-lg">
                      {hour.toString().padStart(2, "0")}h
                    </div>
                    <div className="flex-1 space-y-1">
                      {hourSchedulings.map((s) => {
                        const daysArray = s.days
                          ? s.days.split(",").map((d) => parseInt(d.trim()))
                          : null;
                        const daysLabel = daysArray
                          ? daysArray.map((d) => DAYS_LABELS[d]).join(", ")
                          : "Tous les jours";

                        return (
                          <div
                            key={s.id}
                            className="flex items-center justify-between text-sm"
                          >
                            <span className="font-medium">{s.report_name}</span>
                            <span className="text-muted-foreground text-xs">{daysLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {hourSchedulings.length} rapport(s)
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

